import React, { useState } from 'react';
import './RecipeVisualizer.css';

interface RecipeStep {
  step_number: number;
  instruction: string;
  ingredients?: string[];
  image_prompt?: string;
  camera_params?: {
    angle: string;
    fov: number;
    lighting: string;
    composition: string;
  };
}

interface GeneratedImage {
  step_number: number;
  image_url: string;
  description: string;
}

const RecipeVisualizer: React.FC = () => {
  const [recipeText, setRecipeText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedSteps, setParsedSteps] = useState<RecipeStep[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [currentStep, setCurrentStep] = useState<'input' | 'processing' | 'results'>('input');
  const [error, setError] = useState<string | null>(null);

  const handleProcessRecipe = async () => {
    if (!recipeText.trim()) {
      setError('Please enter a recipe to process');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setCurrentStep('processing');

    try {
      const response = await fetch('http://localhost:8000/api/process-recipe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipe: recipeText }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setParsedSteps(data.recipe.parsed_steps);
        setGeneratedImages(data.recipe.generated_images);
        setCurrentStep('results');
      } else {
        throw new Error(data.error || 'Failed to process recipe');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setCurrentStep('input');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setRecipeText('');
    setParsedSteps([]);
    setGeneratedImages([]);
    setCurrentStep('input');
    setError(null);
  };

  const sampleRecipe = `Chocolate Chip Cookies

Ingredients:
- 2 1/4 cups all-purpose flour
- 1 tsp baking soda
- 1 tsp salt
- 1 cup butter, softened
- 3/4 cup granulated sugar
- 3/4 cup brown sugar
- 2 large eggs
- 2 tsp vanilla extract
- 2 cups chocolate chips

Instructions:
1. Preheat oven to 375°F (190°C).
2. In a bowl, whisk together flour, baking soda, and salt.
3. In a large bowl, cream together butter and sugars until fluffy.
4. Beat in eggs one at a time, then stir in vanilla.
5. Gradually blend in the flour mixture.
6. Stir in chocolate chips.
7. Drop rounded tablespoons of dough onto ungreased cookie sheets.
8. Bake for 9-11 minutes until golden brown.
9. Cool on baking sheets for 2 minutes, then transfer to wire racks.`;

  return (
    <div className="recipe-visualizer">
      {currentStep === 'input' && (
        <div className="input-section">
          <h2>Enter Your Recipe</h2>
          <div className="sample-recipe">
            <button
              onClick={() => setRecipeText(sampleRecipe)}
              className="sample-button"
            >
              Try Sample Recipe: Chocolate Chip Cookies
            </button>
          </div>
          <textarea
            value={recipeText}
            onChange={(e) => setRecipeText(e.target.value)}
            placeholder="Paste your recipe here..."
            className="recipe-input"
            rows={15}
          />
          {error && <div className="error-message">{error}</div>}
          <button
            onClick={handleProcessRecipe}
            disabled={isProcessing || !recipeText.trim()}
            className="process-button"
          >
            {isProcessing ? 'Processing...' : '✨ Generate Visual Recipe'}
          </button>
        </div>
      )}

      {currentStep === 'processing' && (
        <div className="processing-section">
          <div className="processing-spinner"></div>
          <h2>Creating Your Visual Recipe</h2>
          <p>🤖 Parsing recipe steps...</p>
          <p>🎨 Generating professional food images...</p>
          <p>📸 Applying perfect lighting and composition...</p>
          <div className="processing-note">
            This may take 30-60 seconds depending on recipe complexity
          </div>
        </div>
      )}

      {currentStep === 'results' && (
        <div className="results-section">
          <div className="results-header">
            <h2>Your Visual Recipe is Ready!</h2>
            <button onClick={handleReset} className="new-recipe-button">
              Create New Recipe
            </button>
          </div>

          <div className="recipe-steps">
            {parsedSteps.map((step, index) => {
              const image = generatedImages.find(img => img.step_number === step.step_number);
              return (
                <div key={step.step_number} className="recipe-step-card">
                  <div className="step-header">
                    <span className="step-number">Step {step.step_number}</span>
                  </div>
                  <div className="step-content">
                    <div className="step-text">
                      <p>{step.instruction}</p>
                      {step.ingredients && step.ingredients.length > 0 && (
                        <div className="step-ingredients">
                          <strong>Ingredients:</strong>
                          <ul>
                            {step.ingredients.map((ingredient, i) => (
                              <li key={i}>{ingredient}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="step-image">
                      {image ? (
                        <img
                          src={image.image_url}
                          alt={image.description}
                          loading="lazy"
                        />
                      ) : (
                        <div className="image-placeholder">
                          🖼️ Image generating...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="export-section">
            <h3>Export Options</h3>
            <div className="export-buttons">
              <button className="export-button">📱 Download Images</button>
              <button className="export-button">📄 Export as PDF</button>
              <button className="export-button">📋 Copy Recipe Card</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeVisualizer;